// Classification feedback — the ground-truth loop (Aug 11).
// One question, two answers: Agree (one click) or Correct (category select +
// optional comment). A correction becomes a HUMAN memory decision server-side:
// it re-labels stored data on the next boot and names every future harvest of
// this shape — the reader is teaching the classifier, and the copy says so.
// Degrades silently on stores without feedback (demo/fixture mode).
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

export default function FeedbackBlock({ cluster }) {
  const [state, setState] = useState('idle'); // idle | correcting | sending | done | hidden
  const [categories, setCategories] = useState([]);
  const [suggested, setSuggested] = useState('');
  const [comment, setComment] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    let live = true;
    api.categories()
      .then((d) => { if (live) setCategories(d.categories || []); })
      .catch(() => { if (live) setState('hidden'); });
    return () => { live = false; };
  }, []);

  if (state === 'hidden') return null;

  async function send(body) {
    setState('sending');
    try {
      const r = await api.clusterFeedback(cluster.cluster_id, body);
      setResult(r);
      setState('done');
    } catch {
      setState('hidden'); // fixture store / older API — the block simply leaves
    }
  }

  if (state === 'done') {
    return (
      <p className="feedback feedback--done">
        Recorded — thank you.
        {result?.memory_updated && ' Your correction is now a permanent labeling decision: stored data re-labels on the next boot, and future runs of this shape resolve to it automatically.'}
      </p>
    );
  }

  return (
    <div className="feedback">
      <span className="feedback__q">Is this classification right?</span>
      <button
        type="button" className="ghost-btn" disabled={state === 'sending'}
        onClick={() => send({ verdict: 'agree', author: 'dashboard' })}
      >
        ✓ Agree
      </button>
      <button
        type="button" className="ghost-btn" disabled={state === 'sending'}
        onClick={() => setState(state === 'correcting' ? 'idle' : 'correcting')}
      >
        ✗ Correct…
      </button>
      {state === 'correcting' && (
        <span className="feedback__form">
          <label className="field field--inline">
            <span>Category</span>
            <select value={suggested} onChange={(e) => setSuggested(e.target.value)}>
              <option value="">pick the right category…</option>
              {categories.map((c) => (
                <option key={c.key} value={c.key} title={c.explain}>{c.label} ({c.key})</option>
              ))}
            </select>
          </label>
          <input
            type="text" className="feedback__comment" placeholder="why? (optional, helps the next reader)"
            value={comment} maxLength={500}
            onChange={(e) => setComment(e.target.value)}
          />
          <button
            type="button" className="ghost-btn" disabled={!suggested || state === 'sending'}
            onClick={() => send({ verdict: 'correct', suggested_category: suggested, comment, author: 'dashboard' })}
          >
            Submit correction
          </button>
        </span>
      )}
    </div>
  );
}
